import React from 'react';
import {
  AnimatedNumber,
  DisclaimerReferenceProps,
  EducationalInfo,
  generateTestId,
  Result,
  TestIdElementType,
  ResultBar,
  useFeatureAppId,
  useComponent,
  TrackingEvent,
  useTrackingFunctions,
  CalculationResult,
} from '@volkswagen-onehub/whitelabel-e-mobility-commons';
import {useTranslation} from '../use-translation';
import {useTenantConfiguration} from '../use-tenant-configuration';
import styled from 'styled-components';
import {
  ChargerTypeValues,
  EducationalInfoData,
} from '@volkswagen-onehub/whitelabel-e-mobility-data-types';

const DURATION_FORMAT = '%%hours%%:%%minutes%%';

const formatDuration = (format: string, minutes: number) =>
  format
    .replace('%%hours%%', Math.floor(minutes / 60).toString())
    .replace('%%minutes%%', `0${Math.round(minutes % 60)}`.slice(-2));

export interface ChargingData {
  label: string; // e.g. 44 kW
  ariaLabel: string; // e.g. Chargeup time using a 44 kW loading station
  chargeUpMinutes: number;
  selectedChargerType: ChargerTypeValues;
}

interface OutputSectionProps {
  chargeTimeResult: ChargingData;
  inView: boolean;
  disclaimer?: DisclaimerReferenceProps;
}

export const OutputSection = (props: OutputSectionProps): JSX.Element => {
  const translations = useTranslation();
  const tenantConfig = useTenantConfiguration();
  const {disclaimer, chargeTimeResult, inView} = props;
  const featureAppId = useFeatureAppId();
  const chargingResultSectionLabelId = `${featureAppId}-chargingResult-section-label`;
  const tracking = useTrackingFunctions();

  const FlagInverted = useComponent('FlagInverted');
  const ButtonInverted = useComponent('ButtonInverted');
  const Icon = useComponent('Icon');

  const translationsEducationalInfo: EducationalInfoData = {
    educationalInfoContent: translations.resultSection.educationalInfoContent,
    educationalInfoLinkText:
      translations.resultSection.educationalInfoLinkText ||
      translations.global.educationalInfoLayer.FAQLink,
    educationalInfoInfoButtonLabel:
      translations.resultSection.educationalInfoInfoButtonLabel,
  };

  // Tracking event for external url
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    tracking[TrackingEvent.ElliWallboxExternalLinkClick]?.({
      label: translations?.resultSection?.weChargeCrossSell?.linkLabel || '',
      link: {
        url: event.currentTarget.href,
        name: translations?.resultSection?.weChargeCrossSell?.linkLabel || '',
      },
    });
    window.open(event.currentTarget.href, '_blank');
  };

  const calculationResult = translations.resultSection.time.replace(
    '%%time%%',
    formatDuration(DURATION_FORMAT, chargeTimeResult.chargeUpMinutes)
  );

  const resultRef = React.useRef<HTMLDivElement>(null);

  return (
    <section aria-label={translations.resultSection.ariaLabel} ref={resultRef}>
      <Result
        topLineTextPosition={'left'}
        topLine={translations.resultSection.simChargeTimeLabel}
        disclaimer={disclaimer}
        educationalInfo={
          <EducationalInfo
            translations={translationsEducationalInfo}
            globalTranslations={translations.global}
            faqSectionTranslations={translations.faqSection}
            faqOrLink={tenantConfig.resultSection}
            inverted={true}
          />
        }
      >
        <ContentWrapper
          aria-labelledby={chargingResultSectionLabelId}
          aria-label={chargeTimeResult.ariaLabel}
        >
          <CalculationResult value={calculationResult} />
          <div
            data-testid={generateTestId({
              featureAppId,
              elementName: 'chargetime-value',
              elementType: TestIdElementType.dynamic,
            })}
          >
            <TextWrapper>
              <AnimatedNumber value={calculationResult} />
            </TextWrapper>
          </div>
          {translations.resultSection.weChargeCrossSell &&
            tenantConfig.resultSection.weChargeCrossSell &&
            chargeTimeResult.selectedChargerType ===
              ChargerTypeValues.socketAC && (
              <>
                <FlagInverted
                  appearance="offer"
                  text={translations.resultSection.weChargeCrossSell.flagLabel}
                  hideIcon={true}
                  testId={generateTestId({
                    featureAppId,
                    elementName: 'wecharge-flag',
                    elementType: TestIdElementType.dynamic,
                  })}
                />
                <ButtonWrapper>
                  <ButtonInverted
                    tag="a"
                    emphasis="text"
                    target="_blank"
                    href={tenantConfig.resultSection.weChargeCrossSell.url}
                    onClick={handleClick}
                    ariaLabel={
                      translations.resultSection.weChargeCrossSell.ariaLabel
                    }
                    testid={generateTestId({
                      featureAppId,
                      elementName: 'wecharge-information',
                      elementType: TestIdElementType.button,
                    })}
                  >
                    {translations.resultSection.weChargeCrossSell.linkLabel}
                    <Icon name="ExternalLink" size="small" ariaHidden />
                  </ButtonInverted>
                </ButtonWrapper>
              </>
            )}
        </ContentWrapper>
      </Result>
      {translations.resultSection.stickyResult && (
        <ResultBar
          inView={inView}
          headline={translations.resultSection.stickyResult.headline}
          value={calculationResult}
          ref={resultRef}
        />
      )}
    </section>
  );
};

const ContentWrapper = styled.div`
  width: 100%;
`;

const ButtonWrapper = styled.div`
  margin-top: ${(props) => props.theme.internal.size.static200};
`;

const TextWrapper = styled.div`
  padding-bottom: ${(props) => props.theme.internal.size.static150};
`;
